/**
 * PLAT-10287 — Linked cross-language selection highlight (Translation Review).
 *
 * A passive, selection-driven tracking aid: selecting words in one pane subtly
 * highlights the corresponding words in the other-language pane. Nothing is
 * persisted; this is purely a reading aid inside the Translation Review
 * workspace (epic decision D9 — opt-in, contained).
 *
 * The cross-language map rides on data that already ships: a translated phrase
 * (`TranslationPhrase`) carries the source `initial_index` values it covers in
 * `originalIndices` (and its lead = the smallest of them, the value stamped on
 * `data-initial-index` in the translated DOM — see `useTranslatedRender`). Both
 * panes are keyed by `data-initial-index` (the translated pane already; the
 * original pane gains the attribute in `TranscriptionText` for this feature),
 * so the resolution below works in a single index space — `initial_index` — with
 * no positional/array-index translation needed.
 *
 * Granularity follows the rendered fragment tiling: the translated pane keys one
 * span per phrase (lead), so a translated-side selection resolves at phrase
 * granularity today and reaches word level once `phrases[]` is finely split and
 * shown in Fragment view (PLAT-10264). The original→translated direction is
 * word-level wherever source words render individually. Sub-fragment token
 * alignment (an individual word inside a multi-word fragment) is a non-goal.
 */

/** Minimal phrase shape the resolver needs. Carries BOTH key spaces so the link
 *  is drift-correct: the source `initial_index` values (integer fallback) and —
 *  PLAT-10611 (F5) — the stable element UUIDs it covers (`originalElementIds`)
 *  plus its single lead UUID (`leadUuid`, the element at `min(originalIndices)`,
 *  the value the translated span stamps on `data-source-uuid`). The id fields are
 *  optional (absent on legacy blobs / when the caller can't derive them) so the
 *  resolver falls back to the integer space. */
export interface LinkedPhrase {
  originalIndices: ReadonlyArray<number>;
  /** PLAT-10611 — the stable element UUIDs this phrase covers (content-only). */
  originalElementIds?: ReadonlyArray<string>;
  /** PLAT-10611 — the lead element's UUID (keys the translated span). */
  leadUuid?: string;
}

export type LinkedPane = 'original' | 'translated';

/** The smallest `initial_index` in a phrase — the value the translated DOM
 *  stamps on `data-initial-index` for the phrase's lead Word. */
function phraseLead(originalIndices: ReadonlyArray<number>): number {
  let lead = originalIndices[0];
  for (const idx of originalIndices) if (idx < lead) lead = idx;
  return lead;
}

/**
 * Resolve a selection in one pane to the set of `initial_index` values to
 * highlight in the OPPOSITE pane. Pure — no DOM, no MobX.
 *
 * - `fromPane: 'original'` — `selected` is a set of source `initial_index`
 *   values. Returns the **leads** of every phrase whose `originalIndices`
 *   intersect `selected` (those leads key the translated spans to light up).
 * - `fromPane: 'translated'` — `selected` is a set of phrase-lead
 *   `initial_index` values (the translated DOM keys spans by lead). Returns the
 *   **union of `originalIndices`** of every phrase whose lead is in `selected`
 *   (those source indices key the original word spans to light up).
 */
export function resolveMirrorInitialIndices(
  selected: ReadonlySet<number>,
  fromPane: LinkedPane,
  phrases: ReadonlyArray<LinkedPhrase>,
): Set<number> {
  const out = new Set<number>();
  if (selected.size === 0) return out;

  for (const phrase of phrases) {
    const indices = phrase.originalIndices;
    if (indices.length === 0) continue;

    if (fromPane === 'original') {
      let intersects = false;
      for (const idx of indices) {
        if (selected.has(idx)) {
          intersects = true;
          break;
        }
      }
      if (intersects) out.add(phraseLead(indices));
    } else {
      if (selected.has(phraseLead(indices))) {
        for (const idx of indices) out.add(idx);
      }
    }
  }
  return out;
}

/**
 * PLAT-10611 (F5) — the STABLE-ID mirror resolver: the drift-correct analogue of
 * {@link resolveMirrorInitialIndices}, operating on `originalElementIds` +
 * `leadUuid` instead of `initial_index`. Pure — no DOM.
 *
 * - `fromPane: 'original'` — `selectedIds` are SOURCE element UUIDs (the original
 *   pane stamps `data-source-uuid = source_uuid ?? id`). Returns the LEAD UUID of
 *   every phrase whose `originalElementIds` intersect `selectedIds` (those key the
 *   translated spans' `data-source-uuid`).
 * - `fromPane: 'translated'` — `selectedIds` are phrase LEAD UUIDs (the translated
 *   spans' `data-source-uuid`). Returns the union of `originalElementIds` of every
 *   phrase whose `leadUuid` is in `selectedIds` (those key the original spans).
 *
 * A phrase without ids (`originalElementIds` / `leadUuid` absent) is skipped here
 * and covered by the integer half — so the union in {@link resolveLinkedMirror}
 * matches migrated words by id and un-migrated ones by integer.
 */
export function resolveMirrorElementKeys(
  selectedIds: ReadonlySet<string>,
  fromPane: LinkedPane,
  phrases: ReadonlyArray<LinkedPhrase>,
): Set<string> {
  const out = new Set<string>();
  if (selectedIds.size === 0) return out;

  for (const phrase of phrases) {
    const ids = phrase.originalElementIds;
    if (!ids || ids.length === 0) continue;

    if (fromPane === 'original') {
      let intersects = false;
      for (const id of ids) {
        if (selectedIds.has(id)) {
          intersects = true;
          break;
        }
      }
      if (intersects && phrase.leadUuid != null) out.add(phrase.leadUuid);
    } else {
      if (phrase.leadUuid != null && selectedIds.has(phrase.leadUuid)) {
        for (const id of ids) out.add(id);
      }
    }
  }
  return out;
}

/** The union of both key spaces the paint step needs. Read-only — the paint
 *  step only iterates them, and callers may pass frozen/readonly sets. */
export interface LinkedMirror {
  /** Stable element UUIDs to highlight in the opposite pane (id-first). */
  mirrorIds: ReadonlySet<string>;
  /** `initial_index` values to highlight (integer fallback, id-less words). */
  mirrorInts: ReadonlySet<number>;
}

/**
 * PLAT-10611 (F5) — resolve a selection (collected as BOTH an id set and an int
 * set, id-first per span) to the mirror keys in the opposite pane. Runs the
 * stable-id resolver over `selectedIds` AND the integer resolver over
 * `selectedInts` and unions them, so migrated words resolve by identity
 * (drift-correct) and un-migrated / id-less words by the positional integer.
 */
export function resolveLinkedMirror(
  selectedIds: ReadonlySet<string>,
  selectedInts: ReadonlySet<number>,
  fromPane: LinkedPane,
  phrases: ReadonlyArray<LinkedPhrase>,
): LinkedMirror {
  const mirrorIds = resolveMirrorElementKeys(selectedIds, fromPane, phrases);
  const mirrorInts = resolveMirrorInitialIndices(
    selectedInts,
    fromPane,
    phrases,
  );

  return { mirrorIds, mirrorInts };
}

// ---------------------------------------------------------------------------
// CSS Custom Highlight API rendering (mirrors the translated-search approach in
// transcriptSearchUtils.ts, with dedicated names so the two never collide).
// ---------------------------------------------------------------------------

/** Highlight name for the source (original) pane's mirrored words. */
export const LINKED_ORIGINAL_HIGHLIGHT = 'transcript-linked-original';
/** Highlight name for the translated pane's mirrored words. */
export const LINKED_TRANSLATED_HIGHLIGHT = 'transcript-linked-translated';
/**
 * Highlight names for the Changes-panel card hover (original / translated
 * pane). Same correspondence semantic — and hue — as the linked-selection
 * highlight, but registered under their own names so a card hover never
 * clobbers a live selection highlight (the registry is keyed by name).
 */
export const CHANGED_CARD_ORIGINAL_HIGHLIGHT =
  'transcript-changed-card-original';
export const CHANGED_CARD_TRANSLATED_HIGHLIGHT =
  'transcript-changed-card-translated';

/**
 * The correspondence hue — soft blue, one source of truth for every "these
 * words/blocks correspond" paint. `LINKED_HIGHLIGHT_COLOR` is the word-level
 * `::highlight()` fill; `LINKED_ROW_TINT` is the lower-alpha wash for a
 * whole-row emphasis (a fully-covered block on Changes-card hover — painting a
 * calm row instead of every word).
 */
export const LINKED_HIGHLIGHT_COLOR = 'rgba(96, 165, 250, 0.25)';
export const LINKED_ROW_TINT = 'rgba(96, 165, 250, 0.1)';

/**
 * Global `::highlight()` rule for the linked highlight. Deliberately subtle and
 * a distinct hue (soft blue) from the workspace's other highlights — playback /
 * search are gold, actions are green — so it never reads as those, and it layers
 * below them (the Highlight API paints by registration; the state highlights use
 * class-based styling that composites on top). Mount once via `createGlobalStyle`.
 * The Changes-card hover names share the rule: same "these words correspond"
 * meaning, same paint.
 */
export const linkedHighlightCss = `
  ::highlight(${LINKED_ORIGINAL_HIGHLIGHT}),
  ::highlight(${LINKED_TRANSLATED_HIGHLIGHT}),
  ::highlight(${CHANGED_CARD_ORIGINAL_HIGHLIGHT}),
  ::highlight(${CHANGED_CARD_TRANSLATED_HIGHLIGHT}) {
    background-color: ${LINKED_HIGHLIGHT_COLOR};
  }
`;

// CSS Custom Highlight API — not in lib.dom.d.ts in many versions; declare the
// minimum shape we use (same pattern as transcriptSearchUtils.ts).
type HighlightCtor = new (...ranges: Range[]) => HighlightLike;
type HighlightLike = { clear(): void };
type HighlightRegistry = {
  set(name: string, highlight: HighlightLike): void;
  delete(name: string): void;
};
type CssWithHighlights = typeof CSS & { highlights: HighlightRegistry };
type WindowWithHighlight = Window & { Highlight: HighlightCtor };

/** Whether the runtime supports the CSS Custom Highlight API. */
export function supportsLinkedHighlightApi(): boolean {
  return (
    typeof CSS !== 'undefined' &&
    'highlights' in (CSS as unknown as Record<string, unknown>) &&
    typeof (window as unknown as { Highlight?: unknown }).Highlight ===
      'function'
  );
}

/** Clear both linked-highlight registrations. Idempotent. */
export function clearLinkedHighlights(): void {
  if (!supportsLinkedHighlightApi()) return;
  const cssH = CSS as unknown as CssWithHighlights;
  cssH.highlights.delete(LINKED_ORIGINAL_HIGHLIGHT);
  cssH.highlights.delete(LINKED_TRANSLATED_HIGHLIGHT);
}

/**
 * Paint whole-span ranges for the given mirror keys under `highlightName`.
 * PLAT-10611 (F5): queries each span by its STABLE `data-source-uuid` FIRST
 * (drift-correct), then by the retained `data-initial-index` integer for id-less
 * words (§6). Ranges are built ONLY from spans inside `container` (range
 * provenance) — this is what keeps the highlight scoped to one pane even though
 * `CSS.highlights` is a single document-global registry (CLAUDE.md #12: multiple
 * transcript instances coexist on the page). Pass an empty mirror to clear just
 * this name.
 */
export function applyLinkedHighlight(
  container: HTMLElement,
  mirror: LinkedMirror,
  highlightName: string,
): void {
  if (!supportsLinkedHighlightApi()) return;
  const cssH = CSS as unknown as CssWithHighlights;
  if (mirror.mirrorIds.size === 0 && mirror.mirrorInts.size === 0) {
    cssH.highlights.delete(highlightName);
    return;
  }
  const ranges: Range[] = [];
  const pushSpan = (span: HTMLElement | null): void => {
    if (!span) return;
    try {
      const range = new Range();
      range.selectNodeContents(span);
      ranges.push(range);
    } catch {
      // selectNodeContents can throw on a detached node; skip it.
    }
  };
  // id-first: migrated words resolve by identity even on a reordered clip.
  for (const uuid of mirror.mirrorIds) {
    pushSpan(
      container.querySelector<HTMLElement>(`[data-source-uuid='${uuid}']`),
    );
  }
  // integer fallback: id-less / un-migrated words.
  for (const idx of mirror.mirrorInts) {
    pushSpan(
      container.querySelector<HTMLElement>(`[data-initial-index='${idx}']`),
    );
  }
  const Highlight = (window as unknown as WindowWithHighlight).Highlight;
  if (ranges.length > 0) {
    cssH.highlights.set(highlightName, new Highlight(...ranges));
  } else {
    cssH.highlights.delete(highlightName);
  }
}

/** An empty mirror — clears a highlight name via {@link applyLinkedHighlight}. */
export const EMPTY_LINKED_MIRROR: LinkedMirror = {
  mirrorIds: new Set(),
  mirrorInts: new Set(),
};
